/**
 * Abrufe für die Vorlagen und Grundregeln des Auftrags an Claude Code
 * (Tabelle Mail_prompt_vorlagen, Einstellung prompt_grundregeln in
 * Mail_app_settings). Nur Admins dürfen lesen und schreiben — die Datenbank
 * prüft das selbst. Die Form der Daten und der Standard stehen in promptStandard.ts.
 */
import { getAppSetting, setAppSetting, supabase } from './supabase';
import { STANDARD_GRUNDREGELN, type PromptVorlage } from './promptStandard';

const SPALTEN = 'id, name, beschreibung, anweisungen, aendert_mail, ist_standard, sortierung, aktiv';

function client() {
  if (!supabase) throw new Error('Supabase nicht konfiguriert — .env prüfen');
  return supabase;
}

/** Fehlertext der Datenbank in Klartext (Zeilenregeln melden sich mit Code 42501). */
function fehler(e: { message: string; code?: string }): Error {
  if (e.code === '42501') return new Error('Nur Admins ändern die Vorlagen.');
  if (e.code === '23514') return new Error('Name, Beschreibung oder Text sind leer oder zu lang.');
  return new Error(e.message);
}

export async function ladeVorlagen(): Promise<PromptVorlage[]> {
  const { data, error } = await client().from('Mail_prompt_vorlagen').select(SPALTEN).order('sortierung').order('name');
  if (error) throw fehler(error);
  return (data ?? []) as PromptVorlage[];
}

export type VorlageEingabe = Pick<PromptVorlage, 'name' | 'beschreibung' | 'anweisungen' | 'aendert_mail'>;

export async function legeVorlageAn(v: VorlageEingabe, sortierung: number): Promise<void> {
  const { error } = await client().from('Mail_prompt_vorlagen').insert({
    name: v.name.trim(), beschreibung: v.beschreibung?.trim() || null, anweisungen: v.anweisungen.trim(),
    aendert_mail: v.aendert_mail, sortierung,
  });
  if (error) throw fehler(error);
}

export async function aendereVorlage(id: string, v: VorlageEingabe): Promise<void> {
  const { error } = await client().from('Mail_prompt_vorlagen').update({
    name: v.name.trim(), beschreibung: v.beschreibung?.trim() || null, anweisungen: v.anweisungen.trim(),
    aendert_mail: v.aendert_mail,
  }).eq('id', id);
  if (error) throw fehler(error);
}

export async function setzeAktiv(id: string, aktiv: boolean): Promise<void> {
  const { error } = await client().from('Mail_prompt_vorlagen').update({ aktiv }).eq('id', id);
  if (error) throw fehler(error);
}

export async function setzeStandard(id: string): Promise<void> {
  const { error } = await client().rpc('mail_prompt_vorlage_standard', { p_id: id });
  if (error) throw fehler(error);
}

export async function loescheVorlage(id: string): Promise<void> {
  const { error } = await client().from('Mail_prompt_vorlagen').delete().eq('id', id);
  if (error) throw fehler(error);
}

/** Reihenfolge festschreiben: die Liste in gewünschter Reihenfolge, nummeriert in Zehnerschritten (nur Geänderte werden geschrieben). */
export async function ordneVorlagen(liste: readonly PromptVorlage[]): Promise<void> {
  const c = client();
  for (const [i, v] of liste.entries()) {
    const soll = (i + 1) * 10;
    if (v.sortierung === soll) continue;
    const { error } = await c.from('Mail_prompt_vorlagen').update({ sortierung: soll }).eq('id', v.id);
    if (error) throw fehler(error);
  }
}

/** Die Grundregeln; ohne Eintrag die eingebauten. */
export async function ladeGrundregeln(): Promise<string> {
  const wert = await getAppSetting<string>('prompt_grundregeln');
  return typeof wert === 'string' && wert.trim() ? wert : STANDARD_GRUNDREGELN;
}

export async function speichereGrundregeln(text: string): Promise<void> {
  await setAppSetting('prompt_grundregeln', text.trim());
}
